import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, FileText, Eye, Rocket, ChevronLeft, ChevronRight } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import type { EmailTemplateV2 } from '../../../../../types/epicengage';
import { cn } from '../../../../../lib/utils';
import { AudienceSelector, type AudienceSelection } from '../../components/v1/blast/AudienceSelector';

// V1: Simplified to 4 steps - no scheduling
const STEPS = ['Audience', 'Template', 'Preview', 'Confirm'];

export default function ManualBlast() {
  const navigate = useNavigate();
  const { merchant, settings } = useMerchant();
  const [step, setStep] = useState(0);
  const [audienceSelection, setAudienceSelection] = useState<AudienceSelection>({
    preset: 'ALL',
    segmentIds: [],
    tagIds: [],
  });
  const [templates, setTemplates] = useState<EmailTemplateV2[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateV2 | null>(null);
  const [audienceCount, setAudienceCount] = useState(0);
  const [suppressedCount, setSuppressedCount] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (merchant) {
      fetchTemplates();
    }
  }, [merchant]);

  const fetchTemplates = async () => {
    if (!merchant) return;

    const { data } = await epicengageSupabase
      .from('email_templates_v2')
      .select('*')
      .eq('merchant_id', merchant.id)
      .eq('type', 'MANUAL')
      .order('updated_at', { ascending: false });

    setTemplates((data || []) as EmailTemplateV2[]);
  };

  const handleSend = async () => {
    if (!merchant || !selectedTemplate) return;

    // Validate template has HTML content
    if (!selectedTemplate.html_cache) {
      toast.error('Template has no HTML content. Please edit and save the template first.');
      return;
    }

    setSending(true);

    try {
      // Call the v1-create-blast edge function - always send immediately
      const response = await fetch(`${process.env.REACT_APP_EMAIL_SUPABASE_URL}/functions/v1/v1-create-blast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_EMAIL_SUPABASE_PUBLISHABLE_KEY}`
        },
        body: JSON.stringify({
          merchant_id: merchant.id,
          template_id: selectedTemplate.id,
          audience: audienceSelection.segmentIds.length > 0 ? 'SEGMENT' : (audienceSelection.preset || 'ALL'),
          segment_ids: audienceSelection.segmentIds,
          tag_ids: audienceSelection.tagIds,
          scheduled_at: null // Always send now
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send blast');
      }

      // Check if there were any send failures
      if (result.send_result?.failed > 0) {
        toast.warning(`Sent to ${result.send_result.sent} recipients, ${result.send_result.failed} failed.`);
      } else {
        toast.success(`Email sent to ${result.total_recipients} recipients.`);
      }
      
      navigate('/clientpanel/enage/analytics');
    } catch (error: any) {
      console.error('Send blast error:', error);
      toast.error('Failed to send: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return audienceCount > 0 && (!!audienceSelection.preset || audienceSelection.segmentIds.length > 0);
      case 1: return !!selectedTemplate;
      case 2: return true;
      case 3: return true;
      default: return false;
    }
  };

  return (
    <V1Layout>
      <div className="p-8 max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Send className="h-8 w-8" />
            Manual Blast
          </h1>
          <p className="text-muted-foreground mt-1">
            Send a one-time email to your customers
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center mb-8 flex-wrap gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                i <= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              )}>
                {i + 1}
              </div>
              <span className={cn(
                'ml-2 text-sm',
                i <= step ? 'text-foreground' : 'text-muted-foreground'
              )}>
                {s}
              </span>
              {i < STEPS.length - 1 && (
                <div className={cn(
                  'w-8 h-0.5 mx-2',
                  i < step ? 'bg-primary' : 'bg-muted'
                )} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="pt-6">
            {step === 0 && merchant && (
              <AudienceSelector
                merchantId={merchant.id}
                value={audienceSelection}
                onChange={setAudienceSelection}
                recipientCount={audienceCount}
                onRecipientCountChange={setAudienceCount}
                suppressedCount={suppressedCount}
                onSuppressedCountChange={setSuppressedCount}
              />
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Choose Template
                </h3>
                {templates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No templates yet</p>
                    <Button onClick={() => navigate('/clientpanel/enage/templates?type=MANUAL&new=true')}>
                      Create Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={cn(
                          'p-4 border rounded-lg cursor-pointer transition-colors',
                          selectedTemplate?.id === template.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:bg-muted',
                          !template.html_cache && 'opacity-60'
                        )}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <div className="font-medium">{template.name}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {template.subject}
                        </div>
                        {!template.html_cache && (
                          <div className="text-xs text-destructive mt-1">
                            ⚠️ No HTML content - edit template first
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 2 && selectedTemplate && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Preview
                </h3>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Subject</CardTitle>
                    <CardDescription>{selectedTemplate.subject}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg p-4 bg-muted/50 min-h-[200px] overflow-auto max-h-[400px]">
                      {selectedTemplate.html_cache ? (
                        <iframe
                          srcDoc={selectedTemplate.html_cache}
                          className="w-full min-h-[300px] border-0"
                          title="Email Preview"
                        />
                      ) : (
                        <p className="text-destructive text-center py-8">
                          ⚠️ No HTML content. Please edit and save the template in the editor first.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Rocket className="h-5 w-5" />
                  Confirm & Send
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Audience:</span>
                    <span className="ml-2 font-medium">
                      {audienceSelection.segmentIds.length > 0
                        ? `${audienceSelection.segmentIds.length} segment(s)`
                        : audienceSelection.preset || 'All Customers'}
                      {audienceSelection.tagIds.length > 0
                        ? ` + ${audienceSelection.tagIds.length} tag(s)`
                        : ''}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Will send to:</span>
                    <span className="ml-2 font-medium">{audienceCount}</span>
                  </div>
                  {suppressedCount > 0 && (
                    <div>
                      <span className="text-muted-foreground">Suppressed:</span>
                      <span className="ml-2 font-medium text-orange-600">{suppressedCount}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Template:</span>
                    <span className="ml-2 font-medium">{selectedTemplate?.name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sender:</span>
                    <span className="ml-2 font-medium">
                      {settings?.default_sender_email || 'Not configured'}
                    </span>
                  </div>
                </div>
                
                {/* Send confirmation */}
                <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Rocket className="h-5 w-5 text-primary" />
                    <span className="font-medium">Ready to send immediately</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    This email will be sent to all {audienceCount} recipients right now.
                  </p>
                </div>

                {!settings?.default_sender_email && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    ⚠️ No verified sender email configured. Go to Settings to add one.
                  </div>
                )}

                {selectedTemplate && !selectedTemplate.html_cache && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    ⚠️ Template has no HTML content. Please edit and save the template first.
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setStep(s => s - 1)}
                disabled={step === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSend} 
                  disabled={
                    sending || 
                    !settings?.default_sender_email || 
                    audienceCount === 0 ||
                    !selectedTemplate?.html_cache
                  }
                  className="min-w-[120px]"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? 'Sending...' : 'Send Now'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </V1Layout>
  );
}
